import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  formatScore,
  resolveItemScaleMax,
  scaleSigma,
  scoreGapFromTarget,
} from '@/components/assessment-engine/resultsScoreUtils';
import { AE_MUTED, AE_SECTION_TITLE } from '@/components/assessment-engine/layout/aeTheme';
import type { AeQuestionScoreItem, AeSectionScore } from '@/types/assessmentEngine';

type SectionWithGap = AeSectionScore & { gap?: number | null };

interface ResultsStrengthsWeaknessesProps {
  sections: SectionWithGap[];
  questions: AeQuestionScoreItem[];
  scaleMax: number;
  /** Classify vs B1 when true; otherwise vs each item’s own max. */
  useBenchmark: boolean;
  b1BySectionId?: Record<string, { mean: number | null; stdDev?: number | null }>;
}

type ScoredQuestion = {
  key: string;
  sectionId: string;
  label: string;
  /** Gap used for ±1σ classification (benchmark when available). */
  gap: number;
  sigma: number;
  /** Always available for nesting under a flagged dimension (falls back to gap-to-max). */
  supportGap: number;
};

type DimensionInsight = {
  sectionId: string;
  name: string;
  gap: number;
  questions: ScoredQuestion[];
};

function signedGap(gap: number): string {
  const n = formatScore(gap);
  return gap > 0 ? `+${n}` : n;
}

/**
 * Questions that support a flagged dimension.
 * A dimension that clears ±1σ must list questions from that same section.
 */
function supportingQuestions(
  sectionId: string,
  all: ScoredQuestion[],
  kind: 'strength' | 'weakness'
): ScoredQuestion[] {
  const inSection = all.filter((q) => q.sectionId === sectionId);
  if (!inSection.length) return [];

  const strict = inSection.filter((q) =>
    kind === 'strength' ? q.gap > q.sigma : q.gap < -q.sigma
  );
  if (strict.length) {
    return [...strict].sort((a, b) => (kind === 'strength' ? b.gap - a.gap : a.gap - b.gap));
  }

  // Same direction on classification gap (e.g. all mildly positive while mean clears 1σ).
  const sameDirection = inSection.filter((q) =>
    kind === 'strength' ? q.gap > 0 : q.gap < 0
  );
  if (sameDirection.length) {
    return [...sameDirection].sort((a, b) =>
      kind === 'strength' ? b.gap - a.gap : a.gap - b.gap
    );
  }

  // Fallback: best / worst support gaps in the section (gap-to-max when B1 missing on items).
  return [...inSection].sort((a, b) =>
    kind === 'strength' ? b.supportGap - a.supportGap : a.supportGap - b.supportGap
  );
}

function InsightBlock({
  heading,
  intro,
  insights,
  questionLead,
}: {
  heading: string;
  intro: string;
  insights: DimensionInsight[];
  questionLead: string;
}) {
  if (!insights.length) return null;
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">{heading}</h3>
      <p className="text-sm text-[var(--color-surface-text)]">{intro}</p>
      {insights.map((dim) => (
        <div key={dim.sectionId} className="space-y-1.5 text-sm text-[var(--color-surface-text)]">
          <p>
            <span className="font-medium">{dim.name}</span>
            {' — '}
            {signedGap(dim.gap)}
          </p>
          {dim.questions.length > 0 ? (
            <>
              <p className="text-[var(--color-secondary-text)]">{questionLead}</p>
              <ul className="list-disc space-y-1 pl-5">
                {dim.questions.map((q) => (
                  <li key={q.key}>
                    {q.label}
                    <span className="text-[var(--color-secondary-text)]">
                      {' '}
                      ({signedGap(q.gap)})
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Conversational strengths / weaknesses: only ±1σ noteworthy dimensions,
 * each with supporting questions from that same dimension.
 */
export const ResultsStrengthsWeaknesses: React.FC<ResultsStrengthsWeaknessesProps> = ({
  sections,
  questions,
  scaleMax,
  useBenchmark,
  b1BySectionId = {},
}) => {
  const { t } = useLanguage();
  const defaultSigma = scaleSigma(scaleMax);

  const scoredQuestions: ScoredQuestion[] = [];
  for (const q of questions) {
    if (q.is_na || q.score == null) continue;
    const itemMax = resolveItemScaleMax(q.question_type, q.scale_max, scaleMax);
    const gapToMax = itemMax != null ? scoreGapFromTarget(q.score, itemMax) : null;
    const gapToBench = useBenchmark
      ? scoreGapFromTarget(q.score, q.benchmark_score)
      : null;

    // Classification gap prefers benchmark; support gap always needs a value for nesting.
    const gap = gapToBench ?? gapToMax;
    const supportGap = gapToMax ?? gapToBench;
    if (gap == null || supportGap == null) continue;

    const label = q.statement_label?.trim()
      ? `${q.display_number} ${q.statement_label}`
      : `${q.display_number} ${q.question_text}`;

    scoredQuestions.push({
      key: `q:${q.question_id}:${q.statement_key ?? 'scalar'}`,
      sectionId: q.section_id,
      label,
      gap,
      sigma: scaleSigma(itemMax ?? scaleMax),
      supportGap,
    });
  }

  const strengthDims: DimensionInsight[] = [];
  const weaknessDims: DimensionInsight[] = [];

  for (const sec of sections) {
    const peer = b1BySectionId[sec.section_id];
    const gap = useBenchmark
      ? scoreGapFromTarget(sec.mean_score, peer?.mean ?? null)
      : (sec.gap ?? null);
    if (gap == null) continue;

    const sigma =
      useBenchmark && peer?.stdDev != null && peer.stdDev > 0
        ? peer.stdDev
        : (() => {
            const maxes = questions
              .filter((q) => q.section_id === sec.section_id)
              .map((q) => resolveItemScaleMax(q.question_type, q.scale_max, scaleMax))
              .filter((m): m is number => m != null && m > 0);
            if (!maxes.length) return defaultSigma;
            return scaleSigma(maxes.reduce((a, b) => a + b, 0) / maxes.length);
          })();

    if (gap > sigma) {
      strengthDims.push({
        sectionId: sec.section_id,
        name: sec.section_name,
        gap,
        questions: supportingQuestions(sec.section_id, scoredQuestions, 'strength'),
      });
    } else if (gap < -sigma) {
      weaknessDims.push({
        sectionId: sec.section_id,
        name: sec.section_name,
        gap,
        questions: supportingQuestions(sec.section_id, scoredQuestions, 'weakness'),
      });
    }
  }

  strengthDims.sort((a, b) => b.gap - a.gap);
  weaknessDims.sort((a, b) => a.gap - b.gap);

  const hasAny = strengthDims.length > 0 || weaknessDims.length > 0;

  return (
    <section className="mt-6 min-w-0">
      <h2 className={`${AE_SECTION_TITLE} mb-2`}>
        {t('assessmentEngine.results.strengthsWeaknesses')}
      </h2>
      {!hasAny ? (
        <p className={`${AE_MUTED} text-sm`}>
          {t('assessmentEngine.results.nothingNoteworthy')}
        </p>
      ) : (
        <div className="space-y-6">
          <InsightBlock
            heading={t('assessmentEngine.results.strengths')}
            intro={t('assessmentEngine.results.strengthsIntro')}
            insights={strengthDims}
            questionLead={t('assessmentEngine.results.strengthQuestionsLead')}
          />
          <InsightBlock
            heading={t('assessmentEngine.results.weaknesses')}
            intro={t('assessmentEngine.results.weaknessesIntro')}
            insights={weaknessDims}
            questionLead={t('assessmentEngine.results.weaknessQuestionsLead')}
          />
        </div>
      )}
    </section>
  );
};
